import { Button, Input } from "antd";
import { useSendEmailForVerifyMutation } from "./register.service";
import { useContext, useEffect, useMemo, useState } from "react";
import { CreateAccountContext } from "../../provider/createAccountProvider";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function validateEmail(value) {
  if (!value || value.trim() === "") {
    return "Please enter your email address";
  }
  if (!EMAIL_REGEX.test(value.trim())) {
    return "Please enter a valid email address";
  }
  return "";
}

export default function EmailVerifyPage() {
  const { verifyEmail, setVerifyEmail } = useContext(CreateAccountContext);
  const navigate = useNavigate();
  const location = useLocation();

  const emailFromState = location.state?.email || "";
  const emailAlreadySent = !!location.state?.emailAlreadySent;
  const emailFromStorage = useMemo(() => {
    try {
      return sessionStorage.getItem("sfbb_verify_email") || "";
    } catch (_) {
      return "";
    }
  }, []);

  const email = verifyEmail || emailFromState || emailFromStorage;

  const [isSending, setIsSending] = useState(false);
  const [emailSent, setEmailSent] = useState(emailAlreadySent && !!email);
  const [didAutoSend, setDidAutoSend] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [editError, setEditError] = useState("");

  const [requestSendEmailForVerify, { isLoading }] =
    useSendEmailForVerifyMutation();

  const persistEmail = (nextEmail) => {
    const normalized = nextEmail.trim().toLowerCase();
    setVerifyEmail(normalized);
    try {
      sessionStorage.setItem("sfbb_verify_email", normalized);
    } catch (_) {
      /* ignore */
    }
    return normalized;
  };

  const handleSendResult = (
    result,
    sentEmail,
    { isEmailChange = false } = {}
  ) => {
    if (result?.data === "complete_registration" && result?.sessionId) {
      persistEmail(sentEmail);
      navigate(`/create-restaurant?sessionId=${result.sessionId}`);
      return true;
    }

    if (result?.data === "payment") {
      persistEmail(sentEmail);
      navigate("/make-payment");
      return true;
    }

    if (result?.data === "sent") {
      persistEmail(sentEmail);
      setEmailSent(true);
      setIsEditingEmail(false);
      setEditError("");
      toast.success(
        isEmailChange
          ? "Verification link sent to your updated email. You can ignore the previous one."
          : "Verification email sent. Please use the link in your inbox to proceed."
      );
      return true;
    }

    if (result?.data === "already_sent") {
      persistEmail(sentEmail);
      setEmailSent(true);
      setIsEditingEmail(false);
      setEditError("");
      toast.info(
        isEmailChange
          ? "A link was already sent to this email. Please check your inbox (and spam)."
          : "Email already sent. Please check your inbox (and spam)."
      );
      return true;
    }

    return false;
  };

  const getSendErrorMessage = (err) =>
    err?.data?.message ||
    err?.error ||
    "Could not send verification email. Please try again.";

  useEffect(() => {
    if (email && email !== verifyEmail) {
      setVerifyEmail(email);
    }
    if (email) {
      try {
        sessionStorage.setItem("sfbb_verify_email", email);
      } catch (_) {
        /* ignore */
      }
    }
  }, [email, verifyEmail, setVerifyEmail]);

  useEffect(() => {
    if (!email) {
      navigate("/create-account", { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (!email || emailAlreadySent || didAutoSend) return;
    setDidAutoSend(true);
    setIsSending(true);
    requestSendEmailForVerify({ email, isSend: true })
      .unwrap()
      .then((result) => handleSendResult(result, email))
      .catch((err) => {
        toast.error(getSendErrorMessage(err));
      })
      .finally(() => setIsSending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per page load when email is ready
  }, [email, emailAlreadySent, didAutoSend, requestSendEmailForVerify]);

  const handleResendEmailForVerify = async () => {
    if (isSending || isLoading || !email || isEditingEmail) return;
    setIsSending(true);
    try {
      const result = await requestSendEmailForVerify({
        email,
        isSend: true,
      }).unwrap();
      handleSendResult(result, email);
    } catch (err) {
      toast.error(getSendErrorMessage(err));
    } finally {
      setIsSending(false);
    }
  };

  const handleStartEditEmail = () => {
    setEditEmail(email);
    setEditError("");
    setIsEditingEmail(true);
  };

  const handleCancelEditEmail = () => {
    setIsEditingEmail(false);
    setEditEmail("");
    setEditError("");
  };

  const handleSendToUpdatedEmail = async () => {
    const validationError = validateEmail(editEmail);
    if (validationError) {
      setEditError(validationError);
      toast.error(validationError);
      return;
    }

    const nextEmail = editEmail.trim().toLowerCase();
    if (nextEmail === email.toLowerCase()) {
      setIsEditingEmail(false);
      setEditError("");
      return;
    }

    setEditError("");
    setIsSending(true);
    try {
      const result = await requestSendEmailForVerify({
        email: nextEmail,
        isSend: true,
      }).unwrap();
      handleSendResult(result, nextEmail, { isEmailChange: true });
    } catch (err) {
      const message = getSendErrorMessage(err);
      setEditError(message);
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  };

  if (!email) return null;

  const busy = isLoading || isSending;

  return (
    <div className="register-signincontainer signup-page verify-page">
      <div className="saas-card verify-card">
        <div className="verify-brand">
          <div className="verify-brand-title">SFBB Pro</div>
          <div className="verify-brand-sub">The ultimate food safety system</div>
        </div>

        {!emailSent ? (
          <div className="verify-body">
            <div className="verify-heading">Sending verification link</div>
            <p className="verify-copy">We’re sending a link to:</p>
            <div className="verify-email">{email}</div>
            <Button
              size="large"
              block
              loading={busy}
              disabled
              className="signup-cta-btn"
            >
              SENDING...
            </Button>
            <p className="verify-hint">Please wait a moment…</p>
          </div>
        ) : (
          <div className="verify-body">
            <div className="verify-status-pill">Email sent</div>
            <div className="verify-heading">Check your inbox</div>

            {isEditingEmail ? (
              <>
                <p className="verify-copy">Enter the correct email address:</p>
                <Input
                  size="large"
                  type="email"
                  value={editEmail}
                  onChange={(e) => {
                    setEditEmail(e.target.value);
                    if (editError) setEditError("");
                  }}
                  onPressEnter={handleSendToUpdatedEmail}
                  placeholder="you@example.com"
                  disabled={busy}
                  status={editError ? "error" : undefined}
                  className="signup-email-input"
                  autoFocus
                />
                {editError ? (
                  <div className="signup-error">{editError}</div>
                ) : null}
                <p className="verify-hint left">
                  We’ll send a new verification link. You can ignore the
                  previous email.
                </p>
                <div className="verify-actions">
                  <Button
                    size="large"
                    block
                    loading={busy}
                    disabled={busy}
                    onClick={handleSendToUpdatedEmail}
                    className="signup-cta-btn"
                  >
                    {busy ? "SENDING..." : "SEND NEW LINK"}
                  </Button>
                  <Button
                    size="large"
                    block
                    disabled={busy}
                    onClick={handleCancelEditEmail}
                    className="verify-secondary-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="verify-copy">We’ve sent a verification link to:</p>
                <div className="verify-email">{email}</div>
                <p className="verify-copy tight">
                  Use the link in that email to continue registration.
                </p>

                <div className="verify-actions">
                  <Button
                    size="large"
                    block
                    loading={busy}
                    disabled={busy}
                    onClick={handleResendEmailForVerify}
                    className="signup-cta-btn"
                  >
                    {busy ? "SENDING..." : "RESEND LINK"}
                  </Button>
                  <Button
                    size="large"
                    block
                    disabled={busy}
                    onClick={handleStartEditEmail}
                    className="verify-secondary-btn"
                  >
                    Wrong email? Change it
                  </Button>
                </div>

                <p className="verify-hint">
                  You can request a new link every 60 seconds
                </p>

                <div className="verify-tip" role="note">
                  <strong>Didn’t get it?</strong>
                  <ul>
                    <li>
                      Check your <strong>Spam</strong> or <strong>Junk</strong>{" "}
                      folder
                    </li>
                    <li>
                      Add <strong>info@sfbbpro.co.uk</strong> to safe senders
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <style>{`
        .verify-page.register-signincontainer {
          background-color: rgb(0, 111, 81);
          color: white;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 1.25rem 1rem;
          box-sizing: border-box;
        }

        .verify-page .saas-card.verify-card {
          max-width: 480px;
          width: 100%;
          background: rgba(255, 255, 255, 0.06);
          backdrop-filter: blur(4px);
          border-radius: 28px;
          padding: 1.5rem 1.5rem 1.35rem;
          box-shadow: 0 25px 50px -10px rgba(0, 0, 0, 0.35);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .verify-brand {
          text-align: center;
          margin-bottom: 1.15rem;
        }

        .verify-brand-title {
          font-size: 1.55rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #ffffff;
          line-height: 1.15;
        }

        .verify-brand-sub {
          margin-top: 0.25rem;
          font-size: 0.88rem;
          color: #d8efe6;
        }

        .verify-body {
          text-align: center;
        }

        .verify-status-pill {
          display: inline-block;
          background: #91ba41;
          color: #1a1a1a;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          padding: 0.35rem 0.85rem;
          border-radius: 999px;
          margin-bottom: 0.75rem;
        }

        .verify-heading {
          font-size: 1.25rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.55rem;
        }

        .verify-copy {
          margin: 0 0 0.55rem;
          color: #e8f6f0;
          font-size: 0.95rem;
          line-height: 1.45;
        }

        .verify-copy.tight {
          margin-top: 0.35rem;
          margin-bottom: 1rem;
          color: #d8efe6;
          font-size: 0.9rem;
        }

        .verify-email {
          display: block;
          word-break: break-word;
          color: #f9d77e;
          font-size: 1.05rem;
          font-weight: 700;
          margin-bottom: 0.35rem;
        }

        .verify-actions {
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
          margin-top: 0.35rem;
        }

        .verify-page .signup-email-input.ant-input {
          border-radius: 999px !important;
          margin-bottom: 0.55rem;
          padding: 0.65rem 1rem;
          font-size: 0.95rem;
          text-align: center;
        }

        .verify-page .signup-cta-btn.ant-btn {
          height: auto !important;
          min-height: 48px;
          border: none !important;
          border-radius: 999px !important;
          background: #f9d77e !important;
          color: #1e3a2a !important;
          font-weight: 700 !important;
          font-size: 0.95rem !important;
          letter-spacing: 0.4px;
        }

        .verify-page .signup-cta-btn.ant-btn:hover:not(:disabled) {
          background: #f5cc62 !important;
          color: #1e3a2a !important;
        }

        .verify-page .signup-cta-btn.ant-btn:disabled {
          opacity: 0.75;
        }

        .verify-secondary-btn.ant-btn {
          height: auto !important;
          min-height: 46px;
          border-radius: 999px !important;
          background: transparent !important;
          border: 1.5px solid rgba(255, 255, 255, 0.35) !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          font-size: 0.92rem !important;
        }

        .verify-secondary-btn.ant-btn:hover:not(:disabled) {
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: #f9d77e !important;
          color: #f9d77e !important;
        }

        .verify-hint {
          margin: 0.75rem 0 0;
          color: rgba(216, 239, 230, 0.75);
          font-size: 0.8rem;
        }

        .verify-hint.left {
          text-align: left;
          margin: 0 0 0.85rem;
        }

        .verify-page .signup-error {
          color: #ffb3b3;
          font-size: 12px;
          margin: -0.2rem 0 0.55rem;
          text-align: left;
          padding-left: 0.75rem;
        }

        .verify-tip {
          text-align: left;
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 14px;
          padding: 0.85rem 1rem;
          margin-top: 1rem;
          color: #e8f6f0;
          font-size: 0.84rem;
          line-height: 1.45;
        }

        .verify-tip strong {
          color: #fff;
        }

        .verify-tip ul {
          margin: 0.4rem 0 0;
          padding-left: 1.1rem;
        }

        .verify-tip li {
          margin-bottom: 0.2rem;
        }

        @media (max-width: 520px) {
          .verify-page .saas-card.verify-card {
            padding: 1.2rem 1rem 1.15rem;
          }
        }
      `}</style>
    </div>
  );
}
